import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { useFlags } from "launchdarkly-react-client-sdk";
import { loadStripe } from "@stripe/stripe-js";

import TopHeader from "components/common/TopHeader";
import PageLoader from "components/common/PageLoader";
import CustomChip from "components/common/Chip";
import { AlertType } from "components/common/Snackbar";
import { usePermissions } from "components/Hook";
import HelpPopover from "./common/Popover";

import PaymentAch from "./PaymentAch";
import PaymentStripeCard from "./PaymentStripeCard";

import { showSnackbar } from "store/actions/Utility";
import { setBreadcrumbLabel } from "store/actions/Breadcrumbs";
import { getPlatformConfig } from "selectors/platformConfig";
import { createFinancialConnectionsSetupIntent, finalizeFinancialConnectionsSetup } from "services/customer";

import {
  HelpOutlineOutlined as HelpIcon,
  AccountBalanceOutlined as AccountBalanceIcon,
  ChevronRight as ChevronRightIcon,
  LockOutlined as LockOutlinedIcon,
} from "@mui/icons-material";

import "./AddPayment.scss";

function AddPayment() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loggedInUser } = usePermissions();
  const { stripeFc = false } = useFlags();
  const platformConfig = useSelector(getPlatformConfig);
  const { payload: platformPayload = {} } = platformConfig || {};

  const paymentType = searchParams.get("type");
  const update = searchParams.get("update");
  const change = searchParams.get("change");
  const openInfo = Boolean(anchorEl);
  const id = openInfo ? "simple-popover" : undefined;

  const isACH = paymentType === "ach";
  const isACHForm = paymentType === "ach-form";
  const isStripeFc = paymentType === "stripe-fc";
  const isCreditCard = paymentType === "card";

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAnchor = () => {
    setAnchorEl(null);
  };

  // Stripe Financial Connections handler
  const handleConnectViaStripeFC = useCallback(async () => {
    try {
      if (!platformPayload.STRIPE_PUBLISHABLE_KEY) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: "Stripe configuration is missing. Please try again later.",
          })
        );
        return;
      }

      setLoading(true);
      const stripe = await loadStripe(platformPayload.STRIPE_PUBLISHABLE_KEY);
      const fcResponse = await createFinancialConnectionsSetupIntent(loggedInUser?.customerId);
      const fcClientSecret = fcResponse?.clientSecret || fcResponse?.client_secret;

      if (!fcClientSecret) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: "Failed to initialize Stripe. Please try again.",
          })
        );
        setLoading(false);
        return;
      }

      setLoading(false);

      const currentUser = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
      const userEmail = currentUser?.email || "";
      const userName = currentUser?.name || currentUser?.firstName || "Customer";

      const { setupIntent, error } = await stripe.collectBankAccountForSetup({
        clientSecret: fcClientSecret,
        params: {
          payment_method_type: "us_bank_account",
          payment_method_data: {
            billing_details: {
              name: userName,
              email: userEmail,
            },
          },
        },
      });

      if (error) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: error.message || "Failed to connect bank account",
          })
        );
        return;
      }

      if (setupIntent && setupIntent.payment_method) {
        setLoading(true);
        const result = await finalizeFinancialConnectionsSetup(currentUser?.customerId, setupIntent.id);
        if (result?.success) {
          dispatch(
            showSnackbar({
              dispatch,
              type: AlertType.success,
              message: "Payment method added successfully!",
            })
          );
          setLoading(false);
          navigate("/main/payment");
        } else {
          dispatch(
            showSnackbar({
              dispatch,
              type: AlertType.error,
              message: "Failed to finalize bank connection. Please try again.",
            })
          );
          setLoading(false);
        }
      } else if (setupIntent && !setupIntent.payment_method) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: "Bank account connection was not completed. Please try again.",
          })
        );
      }
    } catch (error) {
      const errorMessage = error[0]?.description || error?.message || "Something went wrong, please try again";
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: errorMessage,
        })
      );
      setLoading(false);
    }
  }, [platformPayload.STRIPE_PUBLISHABLE_KEY, loggedInUser?.customerId, dispatch, navigate]);

  const handleConnectWithStripeClick = () => {
    if (stripeFc) {
      handleConnectViaStripeFC();
    } else {
      // Fallback: go to ACH form which has Plaid
      setSearchParams({
        type: "ach-form",
        ...(update ? { update: "true" } : {}),
        ...(change ? { change: "true" } : {}),
      });
    }
  };

  const handleEnterBankDetailsClick = () => {
    setSearchParams({ type: "ach-form", ...(update ? { update: "true" } : {}), ...(change ? { change: "true" } : {}) });
  };

  // Auto-trigger Stripe FC when navigated with type=stripe-fc
  const stripeFcTriggered = useRef(false);
  useEffect(() => {
    if (isStripeFc && stripeFc && !stripeFcTriggered.current) {
      stripeFcTriggered.current = true;
      handleConnectViaStripeFC();
    }
  }, [isStripeFc, stripeFc, handleConnectViaStripeFC]);

  const getHeaderTitle = () => {
    if (isACH) {
      if (update) return "Update ACH direct debit";
      if (change) return "Change to ACH direct debit";
      return "Add payment method";
    }
    if (isCreditCard) {
      if (update) return "Update Credit/Debit Card";
      if (change) return "Change to Credit/Debit Card";
      return "Add payment method";
    }
    if (isACHForm) {
      if (update) return "Update ACH direct debit";
      if (change) return "Change to ACH direct debit";
      return "Add payment method";
    }
    if (isStripeFc) return "Change to ACH direct debit";
    return "Payment method";
  };

  const headerTitle = getHeaderTitle();

  useEffect(() => {
    dispatch(setBreadcrumbLabel("/main/payment/add-payment-method", headerTitle));
  }, [headerTitle, dispatch]);

  const renderACHOptionsScreen = () => (
    <div className="payment-content-container">
      <div className="flex flex-col gap-125 w-full">
        <p className="heading-h3-semibold">How would you like to pay?</p>

        <div className="flex flex-col">
          {/* Connect with Stripe */}
          <div
            className="payment-option-row cursor-pointer"
            onClick={handleConnectWithStripeClick}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") handleConnectWithStripeClick();
            }}
          >
            <div className="payment-option-icon-circle">
              <AccountBalanceIcon className="payment-option-icon" />
            </div>
            <div className="payment-option-content">
              <div className="payment-option-title-row">
                <span className="payment-option-title">Connect with Stripe</span>
                <CustomChip label="Recommended" variant="filled" size="small" />
              </div>
              <p className="payment-option-description">
                Log in to your bank account to connect it as a payment method.
              </p>
            </div>
            <ChevronRightIcon className="payment-option-chevron" />
          </div>

          {/* Enter bank details */}
          <div
            className="payment-option-row cursor-pointer"
            onClick={handleEnterBankDetailsClick}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") handleEnterBankDetailsClick();
            }}
          >
            <div className="payment-option-icon-circle">
              <AccountBalanceIcon className="payment-option-icon" />
            </div>
            <div className="payment-option-content">
              <span className="payment-option-title">Enter bank details</span>
              <p className="payment-option-description">Manually enter your bank details.</p>
            </div>
            <ChevronRightIcon className="payment-option-chevron" />
          </div>
        </div>

        <div className="flex items-start gap-025">
          <LockOutlinedIcon
            style={{ width: "16px", height: "16px", color: "var(--color-text-body-primary)", marginTop: "2px" }}
          />
          <p className="para-body-s-regular color-text-body-primary">
            Your payment information is encrypted and securely shared with Stripe for processing. We never store your
            card or bank account information.
          </p>
        </div>
      </div>
    </div>
  );

  if (!paymentType) {
    return <PageLoader />;
  }

  const renderContent = () => {
    // Stripe FC auto-triggers via useEffect, show nothing while it loads
    if (isStripeFc && stripeFc) {
      return null;
    }

    // ACH options screen (new intermediate screen - only when stripeFc is ON)
    if (isACH && stripeFc) {
      return renderACHOptionsScreen();
    }

    // ACH manual form (direct when stripeFc is OFF, or after selecting "Enter bank details")
    if (isACH || isACHForm) {
      return <PaymentAch update={update} change={change} />;
    }

    if (isCreditCard) {
      return (
        <div className="payment-content-container">
          <div className="alert-container">
            <p className="para-body-m-medium light-grey-text">
              Card payments include a surcharge. Pay with{" "}
              <span
                className="cursor-pointer color-text-action"
                onClick={() => navigate("/main/payment/add-payment-method?type=ach")}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") navigate("/main/payment/add-payment-method?type=ach");
                }}
              >
                ACH Direct Debit
              </span>{" "}
              to avoid this fee.
            </p>
          </div>

          <p className="heading-h3-semibold mb-1">Enter your card details</p>
          <div className="w-full custom-stripe-container">
            <PaymentStripeCard update={update} change={change} />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Helmet>
        <title>Edge | Add Payment Method</title>
      </Helmet>

      {loading && <PageLoader />}

      <div className="payment-container">
        <TopHeader
          title={headerTitle}
          backIcon="/main/payment"
          secondaryBtn="Need help?"
          secondaryBtnIcon={<HelpIcon className="button-secondary-icon" />}
          secondaryBtnClick={handleClick}
          secondaryBtnVariant="text"
        />

        <HelpPopover id={id} open={openInfo} anchorEl={anchorEl} onClose={handleCloseAnchor} />

        {renderContent()}
      </div>
    </>
  );
}

export default AddPayment;
